import React, { useState } from "react";

// Define types for scene data
interface SceneData {
  id: number;
  imagePrompt: string;
  imageStyle: string;
  narrationText: string;
  voiceProvider: string;
  voiceCharacter: string;
  generatedImage: string | null;
  audioUrl: string | null;
  isPlaying: boolean;
}

// Define type for voice character options
interface VoiceCharacterOptions {
  [key: string]: string[];
}

interface VoiceSynthesisCreaternProps {
  onBack: () => void;
}

const VoiceSynthesisCreator: React.FC<VoiceSynthesisCreaternProps> = ({
  onBack,
}) => {
  const [sceneData, setSceneData] = useState<SceneData[]>([
    {
      id: 1,
      imagePrompt: "",
      imageStyle: "自然",
      narrationText: "",
      voiceProvider: "にじボイス",
      voiceCharacter: "",
      generatedImage: null,
      audioUrl: null,
      isPlaying: false,
    },
    // 追加のシーン用のデータ構造（初期状態）
    {
      id: 2,
      imagePrompt: "",
      imageStyle: "自然",
      narrationText: "",
      voiceProvider: "にじボイス",
      voiceCharacter: "",
      generatedImage: null,
      audioUrl: null,
      isPlaying: false,
    },
    {
      id: 3,
      imagePrompt: "",
      imageStyle: "自然",
      narrationText: "",
      voiceProvider: "にじボイス",
      voiceCharacter: "",
      generatedImage: null,
      audioUrl: null,
      isPlaying: false,
    },
    {
      id: 4,
      imagePrompt: "",
      imageStyle: "自然",
      narrationText: "",
      voiceProvider: "にじボイス",
      voiceCharacter: "",
      generatedImage: null,
      audioUrl: null,
      isPlaying: false,
    },
    {
      id: 5,
      imagePrompt: "",
      imageStyle: "自然",
      narrationText: "",
      voiceProvider: "にじボイス",
      voiceCharacter: "",
      generatedImage: null,
      audioUrl: null,
      isPlaying: false,
    },
  ]);

  const [activeScene, setActiveScene] = useState<number>(1);

  // スタイルオプション
  const imageStyleOptions: string[] = ["自然", "鮮やか"];

  // 音声プロバイダーオプション
  const voiceProviders: string[] = [
    "にじボイス",
    "ボイスボックス",
    "ElevenLabs",
  ];

  // 各プロバイダーの音声キャラクターオプション（サンプル）
  const voiceCharacterOptions: VoiceCharacterOptions = {
    にじボイス: ["キャラクター1", "キャラクター2", "キャラクター3"],
    ボイスボックス: ["声優A", "声優B", "声優C"],
    ElevenLabs: ["Rachel", "Adam", "Domi", "Bella", "Antoni"],
  };

  // 現在のシーンのデータを更新する関数
  const updateSceneData = (
    key: keyof SceneData,
    value: string | boolean | null
  ): void => {
    const updatedSceneData = [...sceneData];
    updatedSceneData[activeScene - 1][key] = value as never;
    setSceneData(updatedSceneData);
  };

  // 画像生成ハンドラー（模擬）
  const handleGenerateImage = (): void => {
    // API呼び出しをシミュレート
    setTimeout(() => {
      const updatedSceneData = [...sceneData];
      updatedSceneData[
        activeScene - 1
      ].generatedImage = `/api/placeholder/350/200`;
      setSceneData(updatedSceneData);
    }, 1000);
  };

  // 音声生成ハンドラー（模擬）
  const handleGenerateVoice = (): void => {
    // API呼び出しをシミュレート
    setTimeout(() => {
      const updatedSceneData = [...sceneData];
      updatedSceneData[activeScene - 1].audioUrl = "sample-audio-url";
      setSceneData(updatedSceneData);
    }, 1000);
  };

  // 音声再生切り替えハンドラー
  const togglePlayAudio = (sceneId: number): void => {
    const updatedSceneData = [...sceneData];
    updatedSceneData[sceneId - 1].isPlaying =
      !updatedSceneData[sceneId - 1].isPlaying;
    setSceneData(updatedSceneData);
    // 実際の音声再生処理をここに追加
  };

  // PlayCircleOutlinedアイコンの代わりに使用するSVG
  const PlayIcon: React.FC = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      fill="currentColor"
      viewBox="0 0 16 16"
    >
      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
      <path d="M6.271 5.055a.5.5 0 0 1 .52.038l3.5 2.5a.5.5 0 0 1 0 .814l-3.5 2.5A.5.5 0 0 1 6 10.5v-5a.5.5 0 0 1 .271-.445z" />
    </svg>
  );

  // PauseCircleOutlinedアイコンの代わりに使用するSVG
  const PauseIcon: React.FC = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      fill="currentColor"
      viewBox="0 0 16 16"
    >
      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
      <path d="M5 6.25a1.25 1.25 0 1 1 2.5 0v3.5a1.25 1.25 0 1 1-2.5 0v-3.5zm3.5 0a1.25 1.25 0 1 1 2.5 0v3.5a1.25 1.25 0 1 1-2.5 0v-3.5z" />
    </svg>
  );

  return (
    <div
      className="scene-settings-container"
      style={{ padding: "24px", fontFamily: "Arial, sans-serif" }}
    >
      <h2 style={{ marginBottom: "20px" }}>シーン設定</h2>

      {/* シーンナビゲーション */}
      <div
        className="scene-nav"
        style={{ display: "flex", overflowX: "auto", marginBottom: "20px" }}
      >
        {sceneData.map((scene) => (
          <button
            key={scene.id}
            onClick={() => setActiveScene(scene.id)}
            style={{
              padding: "8px 16px",
              margin: "0 4px 0 0",
              backgroundColor: activeScene === scene.id ? "#1976d2" : "#e0e0e0",
              color: activeScene === scene.id ? "white" : "black",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            シーン #{scene.id}
          </button>
        ))}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "24px" }}>
        {/* 左側：生成済みシーンのプレビュー */}
        <div style={{ flex: "1", minWidth: "250px" }}>
          <div
            style={{
              border: "1px solid #ddd",
              borderRadius: "8px",
              padding: "16px",
              marginBottom: "16px",
            }}
          >
            <h3 style={{ marginTop: 0 }}>シーンプレビュー</h3>
            {sceneData.map(
              (scene) =>
                scene.generatedImage && (
                  <div
                    key={scene.id}
                    style={{
                      marginBottom: "16px",
                      padding: "8px",
                      border: "1px solid #eee",
                      borderRadius: "4px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <div style={{ width: "30%" }}>
                        <img
                          src={scene.generatedImage}
                          alt={`シーン ${scene.id}`}
                          style={{
                            width: "100%",
                            height: "auto",
                            maxHeight: "60px",
                            objectFit: "cover",
                          }}
                        />
                      </div>
                      <div
                        style={{
                          width: "60%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {scene.narrationText ||
                          `シーン #${scene.id} - テキストなし`}
                      </div>
                      <div style={{ width: "10%", textAlign: "right" }}>
                        {scene.audioUrl && (
                          <button
                            onClick={() => togglePlayAudio(scene.id)}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              color: "#1976d2",
                            }}
                          >
                            {scene.isPlaying ? <PauseIcon /> : <PlayIcon />}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )
            )}
          </div>
        </div>

        {/* 右側：現在のシーン設定 */}
        <div style={{ flex: "2", minWidth: "350px" }}>
          <div
            style={{
              border: "1px solid #ddd",
              borderRadius: "8px",
              padding: "16px",
              marginBottom: "16px",
            }}
          >
            <h3 style={{ marginTop: 0 }}>シーン #{activeScene} 設定</h3>

            {/* 画像生成セクション */}
            <div style={{ marginBottom: "24px" }}>
              <h4>画像生成</h4>

              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", marginBottom: "4px" }}>
                  プロンプト
                </label>
                <textarea
                  rows={3}
                  value={sceneData[activeScene - 1].imagePrompt}
                  onChange={(e) =>
                    updateSceneData("imagePrompt", e.target.value)
                  }
                  placeholder="画像生成のプロンプトを入力してください"
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: "4px",
                    border: "1px solid #ddd",
                  }}
                />
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", marginBottom: "4px" }}>
                  スタイル
                </label>
                <div style={{ display: "flex", gap: "8px" }}>
                  {imageStyleOptions.map((style) => (
                    <button
                      key={style}
                      onClick={() => updateSceneData("imageStyle", style)}
                      style={{
                        padding: "6px 12px",
                        backgroundColor:
                          sceneData[activeScene - 1].imageStyle === style
                            ? "#1976d2"
                            : "#e0e0e0",
                        color:
                          sceneData[activeScene - 1].imageStyle === style
                            ? "white"
                            : "black",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                      }}
                    >
                      {style}
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={handleGenerateImage}
                disabled={!sceneData[activeScene - 1].imagePrompt}
                style={{
                  padding: "8px 16px",
                  backgroundColor: !sceneData[activeScene - 1].imagePrompt
                    ? "#cccccc"
                    : "#1976d2",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: !sceneData[activeScene - 1].imagePrompt
                    ? "not-allowed"
                    : "pointer",
                  marginTop: "8px",
                }}
              >
                画像生成
              </button>

              {/* 生成された画像のプレビュー */}
              {sceneData[activeScene - 1].generatedImage && (
                <div style={{ marginTop: "16px" }}>
                  <img
                    src={sceneData[activeScene - 1].generatedImage ?? undefined}
                    alt={`シーン ${activeScene}`}
                    style={{
                      width: "100%",
                      maxHeight: "200px",
                      objectFit: "contain",
                    }}
                  />
                </div>
              )}
            </div>

            <div style={{ margin: "24px 0", borderTop: "1px dashed #eee" }} />

            {/* 音声合成セクション */}
            <div>
              <h4>音声合成</h4>

              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", marginBottom: "4px" }}>
                  ナレーションテキスト
                </label>
                <textarea
                  rows={3}
                  value={sceneData[activeScene - 1].narrationText}
                  onChange={(e) =>
                    updateSceneData("narrationText", e.target.value)
                  }
                  placeholder="ナレーションのテキストを入力してください"
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: "4px",
                    border: "1px solid #ddd",
                  }}
                />
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", marginBottom: "4px" }}>
                  音声プロバイダー
                </label>
                <select
                  value={sceneData[activeScene - 1].voiceProvider}
                  onChange={(e) => {
                    updateSceneData("voiceProvider", e.target.value);
                    updateSceneData("voiceCharacter", ""); // キャラクターをリセット
                  }}
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: "4px",
                    border: "1px solid #ddd",
                  }}
                >
                  {voiceProviders.map((provider) => (
                    <option key={provider} value={provider}>
                      {provider}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", marginBottom: "4px" }}>
                  キャラクター音声
                </label>
                <select
                  value={sceneData[activeScene - 1].voiceCharacter}
                  onChange={(e) =>
                    updateSceneData("voiceCharacter", e.target.value)
                  }
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: "4px",
                    border: "1px solid #ddd",
                  }}
                >
                  <option value="">キャラクターを選択</option>
                  {voiceCharacterOptions[
                    sceneData[activeScene - 1].voiceProvider
                  ]?.map((character) => (
                    <option key={character} value={character}>
                      {character}
                    </option>
                  ))}
                </select>
              </div>

              <button
                onClick={handleGenerateVoice}
                disabled={
                  !sceneData[activeScene - 1].narrationText ||
                  !sceneData[activeScene - 1].voiceCharacter
                }
                style={{
                  padding: "8px 16px",
                  backgroundColor:
                    !sceneData[activeScene - 1].narrationText ||
                    !sceneData[activeScene - 1].voiceCharacter
                      ? "#cccccc"
                      : "#1976d2",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor:
                    !sceneData[activeScene - 1].narrationText ||
                    !sceneData[activeScene - 1].voiceCharacter
                      ? "not-allowed"
                      : "pointer",
                  marginTop: "8px",
                }}
              >
                音声合成
              </button>

              {/* 生成された音声のプレーヤー */}
              {sceneData[activeScene - 1].audioUrl && (
                <div style={{ marginTop: "16px" }}>
                  <button
                    onClick={() => togglePlayAudio(activeScene)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "8px 16px",
                      backgroundColor: "#f0f0f0",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      cursor: "pointer",
                    }}
                  >
                    {sceneData[activeScene - 1].isPlaying ? (
                      <PauseIcon />
                    ) : (
                      <PlayIcon />
                    )}
                    {sceneData[activeScene - 1].isPlaying ? "一時停止" : "再生"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoiceSynthesisCreator;
